import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { MapPinIcon } from '@heroicons/react/24/outline';
import { paramTypes, derivedParamTypes } from '../parameters/fields/fieldTypes';
import MarkdownViewer from '../core/MarkdownViewer';
import CustomModelForm from './CustomModelForm';
import DownloadCustomModelLink from './DownloadCustomModelBtn';
import DuplicateEquationModuleButton from './DuplicateEquationModuleButton';
import DataConfigurationField from './DataConfigurationField';
import { useCustomModel } from '@/stores/nodeStore';

const NodeFields = ({
  caseParams,
  description,
  categories,
  className = '',
  initialCategory,
  targetParam,
  connectorControls,
}) => {
  const initCategory = Object.values(categories).find(cat => cat.categoryName)?.categoryName;
  const hasLocation = !!caseParams?.location;
  const [activeCategory, setActiveCategory] = useState(initialCategory ?? initCategory);
  const customModel = useCustomModel();

  const isCreator = customModel?.is_creator;
  const isNotCreator = customModel?.id && !customModel?.is_creator;

  // Scroll to the target param after the active category's content mounts.
  useEffect(() => {
    if (!targetParam) return;
    const el = document.getElementById(`node-param-${targetParam}`);
    if (el) {
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-amber-300', 'ring-offset-1', 'rounded');
        setTimeout(() => el.classList.remove('ring-2', 'ring-amber-300', 'ring-offset-1', 'rounded'), 1500);
      });
    }
  }, [targetParam, activeCategory]);

  const renderParams = params =>
    params
      .filter(param => paramTypes[param.type])
      .map(paramSchema => {
        const { name, type } = paramSchema;
        const FieldComponent = paramTypes[type];
        const classes = `mx-auto pb-0 mt-6 mb-2 ${className}`;
        const param = caseParams[name];
        const { primary: { value } = {} } = param ?? {};
        const { derived } = value ?? {};

        const Component = derived ? derivedParamTypes[type] : FieldComponent;

        return (
          param &&
          Component && (
            <div key={name} id={`node-param-${name}`} className={classes}>
              <Component name={name} paramSchema={paramSchema} param={param} />
            </div>
          )
        );
      });

  const renderParamsByCategory = categoryName => {
    const paramsByCategory = categories[categoryName]?.params;
    const filteredParams = paramsByCategory?.filter(param => param.type !== 'location');

    if (!filteredParams?.length) {
      return null;
    }

    return renderParams(paramsByCategory);
  };

  return (
    <div className="max-h-[calc(100%_-_80px)] flex flex-col h-full menu-transition-2">
      <div className="flex items-center justify-center gap-2 px-8 pt-2 pb-1">
        {hasLocation && (
          <>
            <Link to="edit-location" className="flex items-center gap-1 min-w-0 hover:opacity-75 transition-opacity">
              <MapPinIcon className="w-4 h-4 text-purple-500 shrink-0" />
              <span className="text-xs text-gray-500 truncate max-w-[180px]">
                {caseParams?.location?.primary?.value?.address || 'Add location'}
              </span>
            </Link>
            <span className="text-gray-300 text-xs select-none">·</span>
          </>
        )}
        <DataConfigurationField />
      </div>
      <ul className="flex border-b border-gray-100 mx-8">
        {Object.values(categories).map(category => {
          const { categoryName, params } = category;
          const showCategory = params.some(({ name: paramName }) => caseParams[paramName] && paramName !== 'location');

          return (
            (showCategory || categoryName === 'Info') && (
              <li key={categoryName} className="inline-block relative">
                <button
                  type="button"
                  onClick={() => {
                    setActiveCategory(categoryName);
                  }}
                  className={`transition ease-in-out delay-100 text-xs text-gray-500 flex items-center px-4 pt-2 pb-3 font-semibold rounded hover:bg-gray-50 hover:text-gray-800 duration-150 ${activeCategory === categoryName ? 'after:content-[""] after:h-[3px] after:bg-info after:w-full after:absolute after:bottom-0 after:left-0 text-gray-800' : 'text-gray-500'}`}
                >
                  {categoryName || 'Simple'}
                </button>
              </li>
            )
          );
        })}
      </ul>
      <div className="overflow-y-auto px-8 flex-grow">
        {Object.values(categories).map(({ categoryName }) => (
          <div className={`${activeCategory === categoryName ? 'visible' : 'hidden'}`} key={categoryName}>
            {categoryName === 'Info' ? (
              <>
                <MarkdownViewer
                  content={description}
                  className="prose mx-auto pb-0 mt-6 mb-2 prose-sm prose-h2:text-sm prose-h3:text-sm prose-p:text-xs prose-pre:bg-gray-50 prose-pre:text-gray-800 prose-pre:border prose-pre:border-gray-200 prose-code:text-gray-800 prose-code:before:hidden prose-code:after:hidden"
                />
                <div className="pt-2">
                  {isCreator && <CustomModelForm customModel={customModel} />}
                  {isNotCreator && <DownloadCustomModelLink customModel={customModel} />}
                  {isNotCreator && <DuplicateEquationModuleButton customModel={customModel} />}
                </div>
              </>
            ) : (
              <>
                {renderParamsByCategory(categoryName)}
                {categoryName === 'Parameters' && connectorControls}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

NodeFields.propTypes = {
  caseParams: PropTypes.object,
  description: PropTypes.string,
  categories: PropTypes.object,
  className: PropTypes.string,
  initialCategory: PropTypes.string,
  targetParam: PropTypes.string,
  connectorControls: PropTypes.node,
};

export default NodeFields;
